import { useState, useEffect, useRef } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../config/firebase";
import { ListMetadata } from "../types/grocery";
import { sharedListMetadataConverter } from "../config/firestoreConverters";
import { createDebug } from "../utils/debug";
import {
  clearSharedListSessionCache,
  readSharedListSessionCache,
  writeSharedListMetadataToSessionCache,
} from "../utils/sharedListSessionCache";
import {
  isRetryableSharedListListenerError,
  isSharedListAccessRevocationError,
  sharedListListenerRetryDelayMs,
} from "../utils/sharedListListenerError";

const debug = createDebug("useSharedListMetadata");

interface UseSharedListMetadataReturn {
  metadata: ListMetadata | null;
  loading: boolean;
  error: string | null;
  isUsingSavedData: boolean;
}

const formatFirebaseError = (error: any): string => {
  // Basic error formatting, can be expanded
  if (!error) return "Unknown error occurred";
  const errorMessage = error.message || error.toString();
  if (errorMessage.includes("permission-denied")) {
    return "Permission denied. You do not have access to this resource.";
  }
  if (errorMessage.includes("not-found")) {
    return "The requested resource was not found.";
  }
  return `Error: ${errorMessage}`;
};

export const useSharedListMetadata = (
  listId: string | undefined,
): UseSharedListMetadataReturn => {
  const initialMetadata = listId
    ? (readSharedListSessionCache(listId)?.metadata ?? null)
    : null;
  const [metadata, setMetadata] = useState<ListMetadata | null>(
    initialMetadata,
  );
  const [loading, setLoading] = useState<boolean>(!initialMetadata);
  const [error, setError] = useState<string | null>(null);
  const [isUsingSavedData, setIsUsingSavedData] = useState(
    initialMetadata !== null,
  );
  const hasUsableMetadataRef = useRef(initialMetadata !== null);
  useEffect(() => {
    if (!listId) {
      setMetadata(null);
      setError("No list ID provided");
      setLoading(false);
      setIsUsingSavedData(false);
      hasUsableMetadataRef.current = false;
      return;
    }

    const cachedMetadata = readSharedListSessionCache(listId)?.metadata ?? null;
    setMetadata(cachedMetadata);
    setError(null);
    setLoading(cachedMetadata === null);
    setIsUsingSavedData(cachedMetadata !== null);
    hasUsableMetadataRef.current = cachedMetadata !== null;
    const listDocRef = doc(db, "sharedGroceryLists", listId).withConverter(
      sharedListMetadataConverter,
    );

    let unsubscribe = (): void => undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryAttempt = 0;
    let stopped = false;

    const subscribe = (): void => {
      unsubscribe = onSnapshot(
        listDocRef,
        { includeMetadataChanges: true },
        (docSnapshot) => {
          const isFirestoreCacheSnapshot =
            docSnapshot.metadata?.fromCache === true;
          if (!isFirestoreCacheSnapshot) retryAttempt = 0;
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            // A projection is never authoritative: retain only its redacted
            // contract fields rather than recreating a legacy identity fallback.
            const nextMetadata: ListMetadata = {
              title: data.title,
              created: data.created,
              ...(data.expires === undefined ? {} : { expires: data.expires }),
              accessConfig: data.accessConfig,
              isPermanent: data.isPermanent,
            };
            setMetadata(nextMetadata);
            if (!isFirestoreCacheSnapshot) {
              writeSharedListMetadataToSessionCache(listId, nextMetadata);
            }
            setError(null);
            setLoading(false);
            setIsUsingSavedData(isFirestoreCacheSnapshot);
            hasUsableMetadataRef.current = true;
          } else if (isFirestoreCacheSnapshot) {
            // An offline SDK cache miss is not proof that the server document was
            // deleted. Keep a restored session projection until the server answers.
            setError(null);
            setLoading(!hasUsableMetadataRef.current);
            setIsUsingSavedData(hasUsableMetadataRef.current);
          } else {
            setError("The shared grocery list does not exist or was deleted.");
            setMetadata(null);
            setLoading(false);
            setIsUsingSavedData(false);
            hasUsableMetadataRef.current = false;
            clearSharedListSessionCache(listId);
          }
        },
        (err) => {
          debug.error("Error in onSnapshot for metadata:", err);
          if (isRetryableSharedListListenerError(err)) {
            setError(null);
            setLoading(!hasUsableMetadataRef.current);
            setIsUsingSavedData(hasUsableMetadataRef.current);
            const delay = sharedListListenerRetryDelayMs(retryAttempt);
            retryAttempt += 1;
            retryTimer = setTimeout(() => {
              if (stopped) return;
              unsubscribe();
              subscribe();
            }, delay);
            return;
          }
          setError(formatFirebaseError(err));
          setMetadata(null);
          setLoading(false);
          setIsUsingSavedData(false);
          hasUsableMetadataRef.current = false;
          if (isSharedListAccessRevocationError(err)) {
            clearSharedListSessionCache(listId);
          }
        },
      );
    };

    subscribe();
    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      unsubscribe();
    };
  }, [listId]);

  return { metadata, loading, error, isUsingSavedData };
};
